/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  InputAdornment, TextField, Tooltip,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { formatDate } from '../../utils/helpers'

/** Shows a freshly issued invite link (the only time it is visible) with a copy button. */
const InviteLinkDialog = ({ link, onClose }) => {
  const [copied, setCopied] = useState(false)
  if (!link) return null

  const copy = async (event) => {
    try {
      await navigator.clipboard.writeText(link.inviteUrl)
      setCopied(true)
    } catch {
      // clipboard blocked (e.g. insecure origin): select the text so the user can copy it
      event.currentTarget.closest('.MuiFormControl-root')?.querySelector('input')?.select()
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite link for {link.invite.email}</DialogTitle>
      <DialogContent>
        {link.emailDelivery === 'SENT' && (
          <Alert severity="success" sx={{ mb: 2 }}>We emailed this link to {link.invite.email}. You can also copy it below.</Alert>
        )}
        {link.emailDelivery === 'FAILED' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The email to {link.invite.email} couldn&apos;t be sent. Share this link yourself, or try Resend on the Members page later.
          </Alert>
        )}
        {link.emailDelivery !== 'SENT' && (
          <DialogContentText sx={{ mb: 2 }}>
            Send this link to {link.invite.email}. Anyone with the link can join as this person, so share it privately.
          </DialogContentText>
        )}
        <TextField
          value={link.inviteUrl}
          InputProps={{
            readOnly: true,
            sx: { fontFamily: 'monospace', fontSize: 13 },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                  <IconButton onClick={copy} aria-label="Copy invite link">
                    {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          onFocus={(e) => e.target.select()}
        />
        <DialogContentText variant="caption" sx={{ display: 'block', mt: 1 }}>
          Expires {formatDate(link.invite.expiresAt)}. Resending from the Members page issues a new link and disables this one.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

export default InviteLinkDialog
